import { useState, useEffect } from 'react';
import { XMarkIcon, PaperClipIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { getDisplayName } from '../utils/i18n';

export default function QuoteForm({ quote, onSave, onCancel }) {
  const [materials, setMaterials] = useState([]);
  const [materialExtras, setMaterialExtras] = useState([]);
  const [formData, setFormData] = useState({
    contactName: '',
    contactEmail: '',
    company: '',
    phone: '',
    description: '',
    material: '',
    materialExtras: [],
    color: { name: '', hex: '' },
    quality: '',
    infill: '',
    quantity: '',
    timeline: ''
  });
  const [newFiles, setNewFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMaterials();
    fetchMaterialExtras();
  }, []);

  useEffect(() => {
    if (quote) {
      setFormData({
        contactName: quote.contactName || '',
        contactEmail: quote.contactEmail || '',
        company: quote.company || '',
        phone: quote.phone || '',
        description: quote.description || '',
        material: typeof quote.material === 'object' ? quote.material._id : quote.material || '',
        materialExtras: quote.materialExtras?.map(me => typeof me === 'object' ? me._id : me) || [],
        color: quote.color || { name: '', hex: '' },
        quality: quote.quality || '',
        infill: quote.infill || '',
        quantity: quote.quantity || '',
        timeline: quote.timeline || ''
      });
    }
  }, [quote]);

  const fetchMaterials = async () => {
    try {
      const response = await api.get('/auth/materials');
      setMaterials(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch materials:', err);
    }
  };

  const fetchMaterialExtras = async () => {
    try {
      const response = await api.get('/auth/material-extras');
      setMaterialExtras(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch material extras:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();

      // Add text fields
      Object.keys(formData).forEach(key => {
        if (key === 'color') {
          formDataToSend.append('color[name]', formData.color.name);
          formDataToSend.append('color[hex]', formData.color.hex);
        } else if (key === 'materialExtras') {
          formData.materialExtras.forEach(extra => {
            formDataToSend.append('materialExtras[]', extra);
          });
        } else if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add new files
      newFiles.forEach(file => {
        formDataToSend.append('files', file);
      });

      await onSave(formDataToSend);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save quote');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleColorChange = (field, value) => {
    setFormData({
      ...formData,
      color: { ...formData.color, [field]: value }
    });
  };

  const handleMaterialExtrasChange = (e) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setFormData({ ...formData, materialExtras: selected });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (newFiles.length + files.length > 5) {
      alert('Maximum 5 files allowed');
      return;
    }
    setNewFiles([...newFiles, ...files]);
  };

  const removeNewFile = (index) => {
    setNewFiles(newFiles.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">
            Edit Quote
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-h-[calc(90vh-120px)] overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contactName" className="block text-sm font-medium text-slate-700 mb-2">
                  Contact Name *
                </label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={250}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="contactEmail" className="block text-sm font-medium text-slate-700 mb-2">
                  Contact Email *
                </label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={250}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  disabled={loading}
                  maxLength={250}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  pattern="\+[0-9]{10,12}"
                  placeholder="+351912345678"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>
            </div>

            {/* Material Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="material" className="block text-sm font-medium text-slate-700 mb-2">
                  Material
                </label>
                <select
                  id="material"
                  name="material"
                  value={formData.material}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                >
                  <option value="">Select material...</option>
                  {materials.map(m => (
                    <option key={m._id} value={m._id}>{getDisplayName(m.name)} ({m.key})</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="materialExtras" className="block text-sm font-medium text-slate-700 mb-2">
                  Material Extras (Hold Ctrl/Cmd for multiple)
                </label>
                <select
                  id="materialExtras"
                  name="materialExtras"
                  multiple
                  value={formData.materialExtras}
                  onChange={handleMaterialExtrasChange}
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white h-24"
                >
                  {materialExtras.map(me => (
                    <option key={me._id} value={me._id}>{getDisplayName(me.name)} ({me.key})</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Color */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="colorName" className="block text-sm font-medium text-slate-700 mb-2">
                  Color Name
                </label>
                <input
                  type="text"
                  id="colorName"
                  value={formData.color.name}
                  onChange={(e) => handleColorChange('name', e.target.value)}
                  disabled={loading}
                  maxLength={100}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="colorHex" className="block text-sm font-medium text-slate-700 mb-2">
                  Color Hex (without #)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="colorHex"
                    value={formData.color.hex}
                    onChange={(e) => handleColorChange('hex', e.target.value.replace('#', ''))}
                    disabled={loading}
                    maxLength={6}
                    pattern="[0-9A-Fa-f]{6}"
                    placeholder="FF0000"
                    className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  />
                  {formData.color.hex && (
                    <div
                      style={{ backgroundColor: `#${formData.color.hex}` }}
                      className="w-12 h-11 border border-slate-300 rounded-lg shadow-inner"
                    ></div>
                  )}
                </div>
              </div>
            </div>

            {/* Print Settings */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label htmlFor="quality" className="block text-sm font-medium text-slate-700 mb-2">
                  Quality (mm)
                </label>
                <select
                  id="quality"
                  name="quality"
                  value={formData.quality}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                >
                  <option value="">Select...</option>
                  <option value="0.05">0.05mm</option>
                  <option value="0.1">0.1mm</option>
                  <option value="0.2">0.2mm</option>
                  <option value="0.3">0.3mm</option>
                  <option value="NOT_SURE">Not Sure</option>
                </select>
              </div>

              <div>
                <label htmlFor="infill" className="block text-sm font-medium text-slate-700 mb-2">
                  Infill (%)
                </label>
                <input
                  type="text"
                  id="infill"
                  name="infill"
                  value={formData.infill}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="20"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="quantity" className="block text-sm font-medium text-slate-700 mb-2">
                  Quantity
                </label>
                <select
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                >
                  <option value="">Select...</option>
                  <option value="1_5">1-5</option>
                  <option value="6_20">6-20</option>
                  <option value="21_50">21-50</option>
                  <option value="51_100">51-100</option>
                  <option value="101_1000">101-1000</option>
                  <option value="1000+">1000+</option>
                  <option value="NOT_SURE">Not Sure</option>
                </select>
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-medium text-slate-700 mb-2">
                  Timeline
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 bg-white"
                >
                  <option value="">Select...</option>
                  <option value="ASAP">ASAP</option>
                  <option value="1W">1 Week</option>
                  <option value="2W">2 Weeks</option>
                  <option value="1M">1 Month</option>
                  <option value="FLEXIBLE">Flexible</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-slate-700 mb-2">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                disabled={loading}
                rows={4}
                maxLength={2000}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 resize-none"
              />
            </div>

            {/* Existing Files */}
            {quote?.files && quote.files.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Existing Files
                </label>
                <div className="space-y-2">
                  {quote.files.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
                      <PaperClipIcon className="h-4 w-4" />
                      <a href={file.s3Url} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 underline">
                        {file.originalName}
                      </a>
                      <span className="text-xs text-slate-400">({(file.fileSize / 1024 / 1024).toFixed(2)} MB)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Files */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Add New Files (max 5 total, 20MB each)
              </label>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                disabled={loading}
                accept=".stl,.step,.obj,.3mf,.jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
              />
              {newFiles.length > 0 && (
                <div className="mt-2 space-y-2">
                  {newFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg">
                      <span className="text-sm text-slate-700">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeNewFile(idx)}
                        disabled={loading}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Saving...' : 'Save Quote'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
